import { useState } from 'react';
import {
  Tabs,
  Form,
  Input,
  Button,
  Typography,
  Descriptions,
  Spin,
  message,
  Card,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useProfile, useUpdateProfile, useChangePassword } from '@hooks/useSettings';
import { useServerVersion } from '@hooks/useServerVersion';
import { ACQ_CLIENT_VERSION, ACQ_CLIENT_RELEASED_AT } from '../../version';
import type { UpdateProfileRequest, ChangePasswordRequest } from '@/types/settings';

function ProfileTab() {
  const { data: acqProfile, isLoading: acqIsLoading } = useProfile();
  const acqUpdateProfile = useUpdateProfile();
  const [acqForm] = Form.useForm();

  const acqHandleSave = async () => {
    const acqValues = await acqForm.validateFields();
    const acqRequest: UpdateProfileRequest = {
      firstName: acqValues.firstName,
      lastName: acqValues.lastName,
      email: acqValues.email,
    };
    await acqUpdateProfile.mutateAsync(acqRequest);
    message.success('Профіль оновлено');
  };

  if (acqIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card style={{ maxWidth: 600 }}>
      <Form
        form={acqForm}
        layout="vertical"
        initialValues={{
          firstName: acqProfile?.firstName ?? '',
          lastName: acqProfile?.lastName ?? '',
          email: acqProfile?.email ?? '',
        }}
      >
        <Form.Item
          name="firstName"
          label="Ім'я"
          rules={[{ required: true, message: "Введіть ім'я" }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item
          name="lastName"
          label="Прізвище"
          rules={[{ required: true, message: 'Введіть прізвище' }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Введіть email' },
            { type: 'email', message: 'Невалідний email' },
          ]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={acqHandleSave}
            loading={acqUpdateProfile.isPending}
          >
            Зберегти
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

function PasswordTab() {
  const acqChangePassword = useChangePassword();
  const [acqForm] = Form.useForm();

  const acqHandleChangePassword = async () => {
    const acqValues = await acqForm.validateFields();
    const acqRequest: ChangePasswordRequest = {
      currentPassword: acqValues.currentPassword,
      newPassword: acqValues.newPassword,
      confirmPassword: acqValues.confirmPassword,
    };
    await acqChangePassword.mutateAsync(acqRequest);
    message.success('Пароль змінено');
    acqForm.resetFields();
  };

  return (
    <Card style={{ maxWidth: 600 }}>
      <Form form={acqForm} layout="vertical">
        <Form.Item
          name="currentPassword"
          label="Поточний пароль"
          rules={[{ required: true, message: 'Введіть поточний пароль' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="Новий пароль"
          rules={[
            { required: true, message: 'Введіть новий пароль' },
            { min: 8, message: 'Мінімум 8 символів' },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Підтвердження пароля"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Підтвердіть пароль' },
            ({ getFieldValue }) => ({
              validator(_, acqValue) {
                if (!acqValue || getFieldValue('newPassword') === acqValue) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Паролі не збігаються'));
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={acqHandleChangePassword}
            loading={acqChangePassword.isPending}
          >
            Змінити пароль
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

function AboutTab() {
  const acqServer = useServerVersion();
  const acqServerVersion = acqServer.data?.version;
  const acqServerReleased = acqServer.data?.releasedAt;
  const acqMatch = !acqServerVersion || acqServerVersion === ACQ_CLIENT_VERSION;

  return (
    <Card style={{ maxWidth: 600 }}>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Система">Квантум</Descriptions.Item>
        <Descriptions.Item label="Версія клієнта">v{ACQ_CLIENT_VERSION}</Descriptions.Item>
        <Descriptions.Item label="Реліз клієнта">{ACQ_CLIENT_RELEASED_AT}</Descriptions.Item>
        <Descriptions.Item label="Версія сервера">
          {acqServer.isLoading
            ? <Spin size="small" />
            : acqServerVersion
              ? <span style={{ color: acqMatch ? undefined : '#d4880a' }}>
                  v{acqServerVersion}{!acqMatch && ' ⚠ розбіжність'}
                </span>
              : <Typography.Text type="secondary">недоступний</Typography.Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Реліз сервера">
          {acqServerReleased ?? <Typography.Text type="secondary">—</Typography.Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Платформа">Electron + React</Descriptions.Item>
        <Descriptions.Item label="Backend">ASP.NET Core 8</Descriptions.Item>
        <Descriptions.Item label="База даних">PostgreSQL 16</Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

export default function SettingsPage() {
  const [acqActiveTab, setMdActiveTab] = useState('profile');

  const acqTabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined /> Профіль
        </span>
      ),
      children: <ProfileTab />,
    },
    {
      key: 'password',
      label: (
        <span>
          <LockOutlined /> Зміна пароля
        </span>
      ),
      children: <PasswordTab />,
    },
    {
      key: 'about',
      label: (
        <span>
          <InfoCircleOutlined /> Про систему
        </span>
      ),
      children: <AboutTab />,
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Налаштування
      </Typography.Title>
      <Tabs
        activeKey={acqActiveTab}
        onChange={setMdActiveTab}
        items={acqTabItems}
      />
    </>
  );
}
